<template>
  <div class="suggested-actions z-10">
    <SuggestedActionsHeader :number-of-actions="actions.length" />
    <vue-scroll
      :ops="{
        rail: { gutterOfSide: '2px', gutterOfEnds: '4px' },
        bar: { size: '4px', keepShow: true },
      }"
      class="suggested-actions-scroll"
    >
      <NoteSuggestedActions
        :suggested-actions="actions"
        @accept="handleAccept"
        @reject="handleReject"
      />
    </vue-scroll>
  </div>
</template>

<script>
import NoteSuggestedActions from '@/components/notes/NoteSuggestedActions';
import SuggestedActionsHeader from '@/components/notes/SuggestedActionsHeader';
import { getQuickCardCreationDraftKey } from '@/utilities/localDrafts';
import { browserStorageSetItem, browserStorageGetItem } from '@/utilities/browser-storage.util';
import { ResourceType } from '@/types/resources';
import generateLink from '@/utilities/generateLink';
import useOpenCard from '@/utilities/composables/useOpenCard';
import EventBus from '@/utilities/eventBus';
import { ActionStatus } from '@superthread-com/api';
import useNotes from '@/utilities/composables/useNotes';

export default {
  name: 'SuggestedActions',
  components: {
    NoteSuggestedActions,
    SuggestedActionsHeader,
  },
  props: {
    actions: {
      type: Array,
      required: true,
    },
    aiNoteId: {
      type: String,
      required: false,
      default: '',
    },
  },
  setup() {
    const { openCard } = useOpenCard();
    const { getSuggestedActionById } = useNotes();

    return { openCard, getSuggestedActionById };
  },
  computed: {
    teamSlug() {
      return this.$store.getters.getTeamSlug;
    },
  },
  methods: {
    handleAccept(action) {
      switch (action.type) {
        case 'create_card':
          this.handleCreateCard(action);
          break;
        case 'update_card':
          this.handleUpdateCard(action);
          break;
        case 'move_card':
          this.handleMoveCard(action);
          break;
        case 'create_comment':
          this.handleCreateComment(action);
          break;
        case 'assign_members':
          this.handleAssignMembers(action);
          break;
        case 'remove_members':
          this.handleRemoveMembers(action);
          break;
        default:
          break;
      }
    },

    handleReject(action) {
      this.updateAiAction(action, ActionStatus.Dismissed);
    },

    updateAiAction({ id: actionId, note_id: noteId }, status) {
      this.$store.dispatch('updateAiAction', {
        actionId,
        noteId,
        aiNoteId: this.aiNoteId,
        payload: { status },
      });
    },

    generateMembersList(members) {
      if (!members || !members.length) return [];

      return members
        .filter((memberId) => {
          const member = this.$store.getters.getMemberById(memberId);
          return member && member.id;
        })
        .map((memberId) => ({ user_id: memberId }));
    },

    handleCreateCard(actionDetails) {
      const {
        create_card: { assignees, ...cardProps },
      } = actionDetails;
      const currentTeamId = this.$store.getters.getTeamID;
      const quickCardDraftKey = getQuickCardCreationDraftKey(currentTeamId);
      const existingQuickCard = browserStorageGetItem(quickCardDraftKey);
      const stateQuickCard = this.$store.getters.getQuickCard;

      // Only create members property if assignees exist and are not empty
      const membersData = assignees?.length ? { members: this.generateMembersList(assignees) } : {};

      // Create new quick card object by merging existing data with new data
      const newQuickCardObject = {
        ...(existingQuickCard || stateQuickCard),
        ...cardProps,
        ...membersData,
      };

      // Update the appropriate storage location
      if (existingQuickCard) {
        browserStorageSetItem(quickCardDraftKey, newQuickCardObject);
      } else {
        this.$store.dispatch('setQuickCardObject', { quickCardObject: newQuickCardObject });
      }

      EventBus.$on('quickCardCreated', () => {
        this.updateAiAction(actionDetails, ActionStatus.Completed);
        EventBus.$off('quickCardCreated');
      });

      this.$store.dispatch('setQuickCard', true);
    },

    generateCardContent(cardId, newContent = '', shouldAppend = false) {
      // Return wrapped content if missing required parameters
      if (!cardId || !newContent) {
        console.error('Invalid parameters for generateCardContent:', { cardId, newContent });
        return `<p>${newContent}</p>`;
      }

      const card = this.$store.getters.getCardById(cardId);
      if (!card) {
        console.error('Card not found with ID:', cardId);
        return `<p>${newContent}</p>`;
      }

      // If appending, combine new content with existing card content
      if (shouldAppend) {
        const cardContent = !!card.content ? `${card.content}<p></p>` : '';
        return `${cardContent}<p>${newContent}</p>`;
      }

      // Otherwise just wrap the new content
      return `<p>${newContent}</p>`;
    },

    handleUpdateCard(actionDetails) {
      const { update_card: cardDetails } = actionDetails;
      const { card_id: cardId, replace_content, append_content, ...rest } = cardDetails;

      if (!cardId) {
        console.error('Missing cardId for update card action:', actionDetails);
        return;
      }

      // Determine the content based on whether we're replacing or appending
      let content = null;
      if (replace_content) {
        content = this.generateCardContent(cardId, replace_content, false);
      } else if (append_content) {
        content = this.generateCardContent(cardId, append_content, true);
      }

      // Update the card with the new content
      this.$store
        .dispatch('updateCard', {
          cardId,
          card: {
            ...rest,
            ...(content && { content }),
          },
        })
        .then(({ data: { card } }) => {
          this.$store.dispatch('localUpdateCardFields', {
            cardId,
            card: card,
          });
          this.updateAiAction(actionDetails, ActionStatus.Completed);
          this.addNotification({
            message: `${this.translate('cardUpdatedSuccessfully')}`,
            status: this.$constants.uiNotificationStatuses.success,
            labels: this.generateNotificationLabels(card),
          });
        });
    },

    handleMoveCard(actionDetails) {
      const {
        move_card: { card_id: cardId },
      } = actionDetails;
      this.addNotification({
        message: `Move card action not yet implemented, card id: ${cardId}`,
      });
    },

    handleCreateComment(actionDetails) {
      const { create_comment: commentDetails } = actionDetails;
      const { card_id: cardId, content } = commentDetails;

      if (!cardId || !content) {
        console.error('Missing cardId or content for create comment action:', actionDetails);
        return;
      }

      this.openCard({ id: cardId, title: '' }, { actionId: actionDetails.id, newComment: true });
    },

    handleAssignMembers(actionDetails) {
      const {
        assign_members: { card_id: cardId },
      } = actionDetails;
      this.addNotification({
        message: `Assign members action not yet implemented, card id: ${cardId}`,
      });
    },

    handleRemoveMembers(actionDetails) {
      const {
        remove_members: { card_id: cardId },
      } = actionDetails;
      this.addNotification({
        message: `Remove members action not yet implemented, card id: ${cardId}`,
      });
    },

    addNotification({
      message,
      labels,
      status = this.$constants.uiNotificationStatuses.warning,
      duration = 4000,
    }) {
      this.$store.dispatch('addUiNotification', {
        message,
        status,
        duration,
        ...(labels && { labels }),
      });
    },

    generateNotificationLabels(card) {
      return [
        {
          text: this.translate('open'),
          type: this.$constants.uiNotificationElements.button,
          callback: () => this.openCard(card),
          params: card,
          buttonStyle: 'important',
        },
        {
          text: this.translate('copyURL'),
          type: this.$constants.uiNotificationElements.button,
          callback: () => this.copyLinkToClipboard(card),
          params: card,
          buttonStyle: 'important',
        },
      ];
    },

    copyLinkToClipboard(card = {}) {
      const cardType = card.sprint_id ? ResourceType.SprintCard : ResourceType.Card;
      const link = generateLink(cardType, this.teamSlug, card.id, card.title);
      navigator?.clipboard.writeText(link);

      this.$store.dispatch('addUiNotification', {
        message: this.translate('linkCopiedToClipboard'),
        icon: 'link-st',
        duration: 4000,
      });
    },
  },
};
</script>

<style lang="scss">
.suggested-actions {
  border-left: 1px solid;
  @apply absolute top-0 right-0 py-3 bg-background-strongest flex flex-col box-border border-standard-border overflow-hidden h-full gap-3;

  .suggested-actions-scroll {
    @apply flex flex-col;
    .__panel {
      @apply px-3;
    }
  }
}
</style>
